import React, { createContext, useState, useEffect, useContext } from "react";
import Cookies from "js-cookie";
import { loginUser, sesionSigueVigente } from "../services/userService";

// Crear contexto de autenticación
const AuthContext = createContext(null);

// Proveedor del contexto de autenticación
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Cargar usuario desde cookies al iniciar
  useEffect(() => {
    const loadUserFromCookies = async () => {
      try {
        const userFromCookie = Cookies.get("user");
        if (userFromCookie) {
          const user = JSON.parse(userFromCookie);

          // Una sesión de alguien que ya no está en el maestro se cierra: cada
          // venta llevaría un cajero que Business Central no conoce. Acuerdo
          // `contrato-hallazgos-ciclo-cri-2026-09-12.md`, H9.
          if (!(await sesionSigueVigente(user))) {
            Cookies.remove("user");
            window.messageApi?.warning?.(
              `El usuario ${user.No} ya no está en el maestro de cajeros. Inicia sesión de nuevo.`
            );
            return;
          }

          setCurrentUser(user);
        }
      } catch (error) {
        console.error("Error al cargar usuario desde cookies:", error);
        // Si hay error, limpiar cookie
        Cookies.remove("user");
      } finally {
        setLoading(false);
      }
    };

    loadUserFromCookies();
  }, []);

  // Iniciar sesión
  const login = async (username, password) => {
    try {
      const user = await loginUser(username, password);

      // Guardar usuario en estado
      setCurrentUser(user);

      // Guardar en cookies por 2 semanas
      Cookies.set("user", JSON.stringify(user), { expires: 14 });

      return user;
    } catch (error) {
      throw error;
    }
  };

  // Refrescar el usuario de la sesión (estado + cookie) a partir de su
  // registro vigente en la base local. Necesario cuando el id local cambia
  // (por ejemplo tras recargar maestros) o cuando cambian sus datos, para que
  // la cookie no quede apuntando a un registro que ya no existe.
  const updateCurrentUser = (user) => {
    if (!user) return;
    setCurrentUser(user);
    Cookies.set("user", JSON.stringify(user), { expires: 14 });
  };

  // Cerrar sesión
  const logout = () => {
    // Limpiar estado
    setCurrentUser(null);

    // Limpiar cookies
    Cookies.remove("user");
  };

  // Verificar si el usuario está autenticado
  const isAuthenticated = () => {
    return !!currentUser;
  };

  const contextValue = {
    currentUser,
    login,
    logout,
    updateCurrentUser,
    isAuthenticated,
    loading,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

// Hook personalizado para usar el contexto de autenticación
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth debe ser usado dentro de un AuthProvider");
  }
  return context;
};

export default AuthContext;
